/**
 * DemoContainer — wraps a demo with standard CSK branding and layout.
 *
 * Single responsibility: provide the shared header (theorem badge, title,
 * description) and footer (key insight) around demo-specific content.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { DemoConfig, DemoTheorem } from "../types.js";
import {
  FONT,
  PRIMARY,
  GRAY,
  THEOREM_COLOR,
  type CanonicalTheoremName,
} from "../design-system/tokens.js";
import { TheoremBadge } from "./TheoremBadge.js";
import { MarkdownPanel } from "./MarkdownPanel.js";

function isTheoremList(theorem: DemoConfig["theorem"]): theorem is readonly DemoTheorem[] {
  return Array.isArray(theorem);
}

function primaryTheoremAccent(theorem: DemoConfig["theorem"]): string {
  const first: DemoTheorem | undefined = isTheoremList(theorem) ? theorem[0] : theorem;
  if (first === undefined) return PRIMARY[600];
  const name = first.name;
  return Object.hasOwn(THEOREM_COLOR, name)
    ? THEOREM_COLOR[name as CanonicalTheoremName]
    : PRIMARY[600];
}

export interface DemoContainerProps {
  readonly config: DemoConfig;
  readonly children?: React.ReactNode;
  /** Optional content width cap for wide interactive demos. */
  readonly maxWidth?: number | string;
}

/**
 * Standard outer shell for all CSK demos.
 *
 * Layout (top to bottom):
 *   - Header: theorem badge, demo title, short description
 *   - Children: demo-specific interactive content
 *   - Footer: key insight callout
 *   - Branding: tagline
 */
export function DemoContainer({
  config,
  children,
  maxWidth = "64rem",
}: DemoContainerProps): React.JSX.Element {
  const accent = primaryTheoremAccent(config.theorem);
  return (
    <div
      style={{
        maxWidth,
        margin: "0 auto",
        padding: "1.5rem clamp(1rem, 3vw, 2rem)",
        fontFamily: FONT.body,
        background: `linear-gradient(180deg, ${GRAY[50]} 0%, #ffffff 76%)`,
        minHeight: "100vh",
      }}
    >
      <header
        style={{
          background: `linear-gradient(135deg, #ffffff 0%, ${accent}0f 100%)`,
          border: `1px solid ${GRAY[300]}`,
          borderRadius: 12,
          borderTop: `3px solid ${accent}`,
          boxShadow: `0 18px 38px rgba(15, 23, 42, 0.08), inset 0 0 0 1px ${accent}0f`,
          marginBottom: "1.25rem",
          padding: "1rem 1.1rem",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {(Array.isArray(config.theorem) ? config.theorem : [config.theorem]).map(
            (t: DemoTheorem) => (
              <TheoremBadge key={t.code} theorem={t} />
            ),
          )}
        </div>
        <h1
          style={{
            color: GRAY[900],
            fontFamily: FONT.heading,
            fontSize: "1.55rem",
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1.2,
            margin: "0.55rem 0 0.3rem",
          }}
        >
          {config.title}
        </h1>
        <p
          style={{
            color: GRAY[600],
            fontSize: "0.95rem",
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {config.description}
        </p>
      </header>

      {children}

      <footer
        style={{
          alignItems: "flex-start",
          background: `linear-gradient(135deg, ${accent}14 0%, ${accent}05 70%)`,
          border: `1px solid ${accent}55`,
          borderLeft: `4px solid ${accent}`,
          borderRadius: 10,
          display: "flex",
          gap: "0.85rem",
          marginTop: "2rem",
          padding: "1rem 1.1rem",
        }}
      >
        <span
          aria-hidden
          style={{
            alignItems: "center",
            background: "#ffffff",
            borderRadius: 999,
            boxShadow: `0 0 0 4px ${accent}22`,
            color: accent,
            display: "inline-flex",
            flexShrink: 0,
            fontFamily: FONT.mono,
            fontSize: "0.95rem",
            fontWeight: 800,
            height: 28,
            justifyContent: "center",
            lineHeight: 1,
            width: 28,
          }}
        >
          ∎
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: accent,
              fontFamily: FONT.mono,
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Key Insight
          </div>
          <MarkdownPanel
            style={{ marginTop: "0.3rem", minWidth: 0, overflowWrap: "anywhere" }}
          >
            {config.insight}
          </MarkdownPanel>
        </div>
      </footer>

      <div
        style={{
          color: GRAY[500],
          fontSize: "0.75rem",
          letterSpacing: "0.01em",
          marginTop: "1.5rem",
          textAlign: "center",
        }}
      >
        Categorical Safety Kernel — Making vulnerabilities structurally inexpressible
      </div>
    </div>
  );
}
